import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { Col, Modal, Row } from "react-bootstrap";
import { LinkSpan } from "../../../shared/components/form/FormElements";
import { Card, CardBody } from "../../../shared/components/Card";
import { Trans, useTranslation } from "react-i18next";
import { getVehicles, post, postApi } from "../../../api/getUser";
import { updateVehiclesList } from "../../../features/vehicles/vehiclesSlice";
import { useToast } from "../../../contexts/ToastContext";
import format from "date-fns/format";
import { NormalSpan } from "..";
import TableTabs from "./components/TableTabs";
import ComparePlansTable from "../ComparePlansTable";
import { ActionIconContainer } from "../../../shared/components/TableElements";
import ActionPopup from "../../../shared/components/ActionPopup";
import EssentialImg from "../../../shared/img/Essential.png";
import AdvancedImg from "../../../shared/img/Advanced.png";
import MediumImg from "../../../shared/img/medium.png";
import EnterpriseImg from "../../../shared/img/enterprise.png";
import SmallImg from "../../../shared/img/small.png";
import {
  CurrentPlanCard,
  AICreditsCard,
  PaymentMethodCard,
} from "./components";
import { formatNumber, getListingDisplayDate } from "../../../shared/helpers";
import EquipmentPurchase from "../components/EquipmentPurchase";
import WexAddonRow from "../components/WexAddonRow";
import TelematicsAddonRow from "../components/TelematicsAddonRow";
import ChangePlanModal from "./components/ChangePlanModal";
import ReactivateSubscriptionModal from "../components/ReactivateSubscriptionModal";
import ErrorPopup from "../../../shared/components/ErrorPopup";
import { useAuth } from "../../../contexts/AuthContext";

const ExistingPlan = ({
  paymentData,
  subPaymentData,
  invoices,
  receipts,
  priceDetailsData,
  topUpPriceDetails,
  subscriptionData,
  perVehiclePrice,
  toggleValue,
  equipmentSubscriptionData,
  pollSubscriptionStatus,
  isLegacyPricing,
  //telematicsData,
}) => {
  const { t } = useTranslation("common");
  const {
    tag,
    integrationsData,
    setIntegrationsData,
    setShowFaultCodes,
    setFetchTelematicsOdo,
  } = useAuth();
  const { showToast } = useToast();
  const dispatch = useDispatch();

  const [showComparePlans, setShowComparePlans] = useState(false);
  const [showNewPlanPopup, setShowNewPlanPopup] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showReactivateSubscription, setShowReactivateSubscription] =
    useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [reactivateError, setReactivateError] = useState("");
  // const [pendingSessionParams, setPendingSessionParams] = useState({
  //   querystring: "create_customer_session",
  //   update_payment: 0,
  // });

  let planType = "";
  let planImg = "";
  const productPlan = subscriptionData?.product;
  const isNewPlan = ["5", "6"].includes(productPlan);
  if (productPlan === "5") {
    planImg = EssentialImg;
    planType = t("essential");
  } else if (productPlan === "6") {
    planType = t("advanced");
    planImg = AdvancedImg;
  } else if (productPlan === "1") {
    planType = t("small_sm");
    planImg = SmallImg;
  } else if (productPlan === "2") {
    planType = t("medium_sm");
    planImg = MediumImg;
  } else if (productPlan === "3") {
    planType = t("enterprise_sm");
    planImg = EnterpriseImg;
  }

  const isAndroid = subscriptionData.isandroid;
  const isIos = subscriptionData.isios;

  //State to show a popup that a user cannot purchase an add on
  // if they purchased the subscription from mobile
  const [showMobileAddOnErrPopup, setShowMobileAddOnErrPopup] = useState(false);
  const [showWexPlanErrPopup, setShowWexPlanErrPopup] = useState(false);

  const isWexEligiblePlan = productPlan === "5" || productPlan === "6";

  const telematicsItemId = subscriptionData?.telematics_renews;
  const fuelCardItemId = subscriptionData?.fuel_card_renews;
  const equipmentSubscriptionId = subscriptionData?.equipment_renews;

  const activeTelematicsProviders = useMemo(
    () => (integrationsData ?? []).filter((i) => i.provider_type === 1),
    [integrationsData],
  );
  const hasActiveTelematicsConnection = activeTelematicsProviders.length > 0;

  //let blueCardText = "";

  const advancedFeatures = [
    //t("everything_in_essential"),
    t("work_order_and_parts_mgmt"),
    t("trip_mgmt"),
    t("priority_support"),
  ];

  // if (isAndroid) {
  //   blueCardText = t("android_plan_msg");
  // } else if (isIos) {
  //   blueCardText = blueCardText = t("apple_plan_msg");
  // } else {
  //   if (["1", "2", "3"].includes(productPlan)) {
  //     blueCardText = t("per_vehicle_billing_structure_msg");
  //   } else if (productPlan === "5") {
  //     blueCardText = `${t("advanced_plan_marketing_text")} ${
  //       advancedFeatures[Math.floor(Math.random() * 3)]
  //     } `;
  //   }
  // }

  const duration =
    subPaymentData?.duration === "year" ? t("year_sm") : t("month_sm");
  const inputDuration = subPaymentData?.duration === "year" ? 2 : 1;
  const quantity = subPaymentData?.quantity;
  const amount = subPaymentData?.amount;
  const vehicleCount =
    !isAndroid && !isIos
      ? quantity || 0
      : subscriptionData?.no_of_vehicles || 0;
  const totalAmount = subPaymentData?.quantity * subPaymentData?.amount;
  const currency = subPaymentData?.currency;

  //If android/ios sub, quantity from invoice details api will not be populated
  const qtyPriceString = isNewPlan
    ? !isAndroid && !isIos
      ? `${quantity} ${
          quantity === 1 ? t("vehicle_sm") : t("vehicles_sm")
        } @ ${currency} ${amount?.toFixed(2)}/${t("vehicle_sm")}`
      : `${subscriptionData.no_of_vehicles} ${
          subscriptionData.no_of_vehicles === 1
            ? t("vehicle_sm")
            : t("vehicles_sm")
        }`
    : "";

  let renewsOn =
    typeof subPaymentData?.current_period_end === "number"
      ? `${
          subscriptionData?.stripe_status === "canceled"
            ? t("cancels_on")
            : t("renews_on")
        } ${format(
          new Date(subPaymentData?.current_period_end * 1000),
          "d MMMM yyyy",
        )}`
      : subPaymentData?.current_period_end;

  if (isAndroid || isIos) {
    renewsOn = `${
      subscriptionData?.stripe_status === "canceled"
        ? t("cancels_on")
        : t("renews_on")
    } ${getListingDisplayDate(subscriptionData?.end_date)}`;
  }

  let creditsInPlan = 0;
  let noOfMonths = subscriptionData?.duration === 1 ? 1 : 12;
  if (subscriptionData?.product === "5") {
    creditsInPlan = 2 * noOfMonths * subscriptionData?.no_of_vehicles;
  } else if (subscriptionData?.product === "6") {
    creditsInPlan = 5 * noOfMonths * subscriptionData?.no_of_vehicles;
  }

  let creditsIncluded = `${creditsInPlan} ${t("credits_sm")}/${duration}`;
  let creditsNextGrant = "";
  if (subscriptionData?.stripe_status === "active") {
    creditsNextGrant = getListingDisplayDate(
      isAndroid || isIos
        ? subscriptionData?.end_date
        : subPaymentData?.current_period_end,
    );
  }

  const handleChangeCancel = () => {
    const oldPlans = ["1", "2", "3"];
    if (oldPlans.includes(subscriptionData.product)) {
      setShowNewPlanPopup(true);
    } else {
      setShowChangePlanModal(true);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivateLoading(true);
    setReactivateError("");
    try {
      const res = await postApi(
        { querystring: "reactivate_subscription" },
        "subscription",
      );
      if (res.success) {
        setShowReactivateSubscription(false);
        //window.location.reload();
        pollSubscriptionStatus(
          (data) => data?.stripe_status === "active",
          5,
          3000,
          () => showToast("success", t("plan_updated_successfully")),
        );
        // pollSubscriptionStatus(
        //   (data) => data?.stripe_status === "active",
        // );
      } else {
        setReactivateError(res.error || t("error_reactivating_subscription"));
      }
    } catch {
      setReactivateError(t("error_reactivating_subscription"));
    } finally {
      setReactivateLoading(false);
    }
  };

  const equipmentUnitPrice = useMemo(() => {
    // let unitPrice = 0;
    // if (inputDuration === 1) {
    //   unitPrice = priceDetailsData?.equipment_monthly_price;
    // } else {
    //   unitPrice = priceDetailsData?.equipment_yearly_price || 0.5;
    // }
    // return unitPrice;

    if (!priceDetailsData) return 0;
    return inputDuration === 2
      ? priceDetailsData.equipment_yearly_price
      : priceDetailsData.equipment_monthly_price;
  }, [inputDuration, priceDetailsData]);

  const telematicsUnitPrice = useMemo(() => {
    if (!priceDetailsData) return 0;
    return inputDuration === 2
      ? priceDetailsData.telematics_yearly_price
      : priceDetailsData.telematics_monthly_price;
  }, [priceDetailsData, inputDuration]);

  const wexUnitPrice = useMemo(() => {
    if (!priceDetailsData) return 0;
    return inputDuration === 2
      ? priceDetailsData.fuel_card_yearly_price
      : priceDetailsData.fuel_card_monthly_price;
  }, [priceDetailsData, inputDuration]);

  const wexStatus = useMemo(() => {
    if (subscriptionData?.subscription_status === "trial") return "locked";
    return subscriptionData?.fuel_card_stripe_status;
  }, [subscriptionData]);

  const equipmentStatus = useMemo(() => {
    if (subscriptionData?.subscription_status === "trial") return "locked";
    return subscriptionData?.equipment_stripe_status;
  }, [subscriptionData]);

  const telematicsAddonTotal =
    Number(telematicsUnitPrice) * Number(subscriptionData?.no_of_vehicles || 0);
  const wexAddonTotal =
    Number(wexUnitPrice) * Number(subscriptionData?.no_of_vehicles || 0);
  const equipmentAddonTotal =
    Number(equipmentUnitPrice) *
    Number(subscriptionData?.no_of_equipments || 0);

  const endDate = useMemo(() => {
    const ts = subscriptionData?.end_date;
    if (!ts) return null;
    return getListingDisplayDate(ts);
  }, [subscriptionData]);

  const wexEndDate = useMemo(() => {
    const ts = subscriptionData?.fuel_card_end_date;
    if (!ts) return null;
    return getListingDisplayDate(ts);
  }, [subscriptionData]);

  const equipmentEndDate = useMemo(() => {
    const ts = subscriptionData?.equipment_end_date;
    if (!ts) return null;
    return getListingDisplayDate(ts);
  }, [subscriptionData]);

  const telematicsStatus = useMemo(() => {
    if (subscriptionData?.subscription_status === "trial") return "locked";
    return subscriptionData?.telematics_stripe_status;
  }, [subscriptionData]);

  const telematicsEndDate = useMemo(() => {
    const ts = subscriptionData?.telematics_end_date;
    if (!ts) return null;
    return getListingDisplayDate(ts);
  }, [subscriptionData]);

  const isNotOldAdvancedUser =
    !isLegacyPricing || (isLegacyPricing && telematicsItemId);

  const telematicsTotalAfterCancellation =
    totalAmount +
    (wexStatus === "active" ? wexAddonTotal : 0) +
    (equipmentStatus === "active" ? equipmentAddonTotal : 0);

  const wexTotalAfterCancellation =
    totalAmount +
    (telematicsStatus === "active" && isNotOldAdvancedUser
      ? telematicsAddonTotal
      : 0) +
    (equipmentStatus === "active" ? equipmentAddonTotal : 0);

  const equipmentTotalAfterCancellation =
    totalAmount +
    (telematicsStatus === "active" && isNotOldAdvancedUser
      ? telematicsAddonTotal
      : 0) +
    (wexStatus === "active" ? wexAddonTotal : 0);

  const handleDeleteTelematicsConnections = async () => {
    for (const intObj of activeTelematicsProviders) {
      try {
        await post("integrations", {
          querystring: "delete_integration",
          provider: intObj.provider,
          provider_type: 1,
        });
      } catch {
        // best-effort cleanup — plan change already succeeded
      }
    }
    // Reset integrations global state
    try {
      setShowFaultCodes(false);
      setFetchTelematicsOdo(false);
      const res = await post("integrations", {
        querystring: "get_integrations_data",
      });
      if (res.success) {
        const { data } = res;
        if (data.length > 0) {
          setIntegrationsData(data);
        } else {
          setIntegrationsData([]);
        }
      } else {
        setIntegrationsData([]);
      }
    } catch {
      // non-critical
    }
  };

  return (
    <div className="d-flex flex-column gap-4">
      <ChangePlanModal
        show={showChangePlanModal}
        onHide={() => setShowChangePlanModal(false)}
        subscriptionData={subscriptionData}
        priceDetailsData={priceDetailsData}
        subPaymentData={subPaymentData}
        //telematicsData={telematicsData}
        onSuccess={() => {
          setShowChangePlanModal(false);
          window.location.reload();
        }}
        onUpdateSuccess={(newCount) => {
          setShowChangePlanModal(false);
          pollSubscriptionStatus(
            (data) => Number(data?.no_of_vehicles) === Number(newCount),
            5,
            3000,
            async () => {
              showToast("success", t("plan_updated_successfully"));
              const updatedVehiclesList = await getVehicles();
              dispatch(updateVehiclesList(updatedVehiclesList));
            },
          );
        }}
        onCancelSuccess={() => {
          setShowChangePlanModal(false);
          pollSubscriptionStatus(
            (data) => data?.stripe_status === "canceled",
            5,
            3000,
            () => showToast("success", t("plan_updated_successfully")),
          );
        }}
        paymentData={paymentData}
        telematicsStatus={telematicsStatus}
        wexStatus={wexStatus}
        equipmentStatus={equipmentStatus}
        telematicsUnitPrice={telematicsUnitPrice}
        wexUnitPrice={wexUnitPrice}
        equipmentUnitPrice={equipmentUnitPrice}
        equipmentQuantity={subscriptionData?.no_of_equipments}
        telematicsItemId={telematicsItemId}
        fuelCardItemId={fuelCardItemId}
        equipmentSubscriptionId={equipmentSubscriptionId}
        isLegacyPricing={isLegacyPricing}
        hasActiveTelematicsConnection={hasActiveTelematicsConnection}
        onDeleteTelematicsConnections={handleDeleteTelematicsConnections}
      />
      <ReactivateSubscriptionModal
        show={showReactivateSubscription}
        onHide={() => {
          setShowReactivateSubscription(false);
          setReactivateError("");
        }}
        inputDuration={inputDuration}
        lineItems={[
          {
            name: `${planType} - ${inputDuration === 1 ? t("monthly") : t("yearly")}`,
            amount: `${currency} ${formatNumber(totalAmount, 2)}/${duration}`,
            detail: quantity
              ? `${quantity} ${t("vehicles_sm")} × ${currency} ${formatNumber(amount || 0, 2)}`
              : undefined,
          },
          ...((telematicsStatus === "active" || telematicsItemId) &&
          isNotOldAdvancedUser
            ? [
                {
                  name: t("telematics"),
                  amount: `${currency} ${formatNumber(telematicsAddonTotal, 2)}/${duration}`,
                  detail: `${subscriptionData?.no_of_vehicles || 0} ${t("vehicles_sm")} × ${currency} ${formatNumber(telematicsUnitPrice, 2)}`,
                },
              ]
            : []),
          ...(wexStatus === "active" || fuelCardItemId
            ? [
                {
                  name: t("wex_fuel_card"),
                  amount: `${currency} ${formatNumber(wexAddonTotal, 2)}/${duration}`,
                  detail: `${subscriptionData?.no_of_vehicles || 0} ${t("vehicles_sm")} × ${currency} ${formatNumber(wexUnitPrice, 2)}`,
                },
              ]
            : []),
          ...(equipmentStatus === "active" || equipmentSubscriptionId
            ? [
                {
                  name: t("small_equipment"),
                  amount: `${currency} ${formatNumber(equipmentAddonTotal, 2)}/${duration}`,
                  detail: `${subscriptionData?.no_of_equipments || 0} ${t("equipment_sm")} × ${currency} ${formatNumber(equipmentUnitPrice, 2)}`,
                },
              ]
            : []),
        ]}
        monthlyTotal={`${currency} ${formatNumber(
          totalAmount +
            (wexStatus === "active" || fuelCardItemId ? wexAddonTotal : 0) +
            ((telematicsStatus === "active" || telematicsItemId) &&
            isNotOldAdvancedUser
              ? telematicsAddonTotal
              : 0) +
            (equipmentStatus === "active" || equipmentSubscriptionId
              ? equipmentAddonTotal
              : 0),
          2,
        )}/${duration}`}
        onConfirm={handleReactivateSubscription}
        loading={reactivateLoading}
        error={reactivateError}
      />
      {showNewPlanPopup && (
        <ActionPopup
          showPopup={showNewPlanPopup}
          setShowPopup={setShowNewPlanPopup}
          handleYes={() => {
            setShowChangePlanModal(true);
            setShowNewPlanPopup(false);
          }}
          handleNo={() => setShowNewPlanPopup(false)}
          message={t("change_sub_to_new_plan_msg")}
          modalSize={"lg"}
        />
      )}
      {showMobileAddOnErrPopup && (
        <ErrorPopup
          showPopup={showMobileAddOnErrPopup}
          setShowPopup={setShowMobileAddOnErrPopup}
          message={
            <Trans
              i18nKey="mobile_add_on_purchase_err_msg"
              t={t}
              components={[<a href={`mailto:support@simply-fleet.com`} />]}
            />
          }
        />
      )}
      {showWexPlanErrPopup && (
        <ErrorPopup
          showPopup={showWexPlanErrPopup}
          setShowPopup={setShowWexPlanErrPopup}
          message={t("wex_plan_ineligible_err_msg")}
        />
      )}
      {showComparePlans && (
        <Modal
          size="xl"
          show={showComparePlans}
          onHide={() => setShowComparePlans(false)}
        >
          <Modal.Header>
            <h4>{t("compare_plans")}</h4>
            <div className="d-flex justify-content-end">
              <ActionIconContainer onClick={() => setShowComparePlans(false)}>
                <i className="fi fi-rr-cross-small" />
              </ActionIconContainer>
            </div>
          </Modal.Header>
          <Modal.Body>
            <ComparePlansTable
              perVehiclePrice={perVehiclePrice}
              toggleValue={toggleValue}
              currency={priceDetailsData?.currency}
              currencyCode={priceDetailsData?.currency_code}
              insideModal={true}
              subscriptionData={subscriptionData}
            />
          </Modal.Body>
        </Modal>
      )}
      <Row className="d-flex justify-content-center">
        <Col className="d-flex justify-content-center">
          <CurrentPlanCard
            planImg={planImg}
            planType={planType}
            currency={currency}
            totalAmount={totalAmount}
            duration={duration}
            qtyPriceString={qtyPriceString}
            renewsOn={renewsOn}
            onChangePlanClick={handleChangeCancel}
            onReactivateClick={() => setShowReactivateSubscription(true)}
            //handleChangeCancel={handleChangeCancel}
            setShowComparePlans={setShowComparePlans}
            isAndroid={isAndroid}
            isIos={isIos}
            stripeStatus={subscriptionData?.stripe_status}
            paymentData={paymentData}
          />
        </Col>
        {/* <Col className="d-flex justify-content-center" md={4}>
          <AICreditsCard
            paymentData={paymentData}
            onAddPaymentMethod={handleChangeCancel}
            creditsIncluded={creditsIncluded}
            creditsNextGrant={creditsNextGrant}
            topUpPriceDetails={topUpPriceDetails}
          />
        </Col> */}
        {/* {paymentData && (
          <Col className="d-flex justify-content-center" md={4}>
            <PaymentMethodCard
              paymentData={paymentData}
              handleChangePlan={handleChangePlan}
            />
          </Col>
        )} */}
      </Row>

      {(wexStatus === "active" ||
        equipmentStatus === "active" ||
        (telematicsStatus === "active" && isNotOldAdvancedUser)) && (
        <Row>
          <Col>
            <MonthlyTotalCard>
              <div>
                <MonthlyTotalLabel>
                  {inputDuration === 2 ? t("yearly_total") : t("monthly_total")}
                </MonthlyTotalLabel>
                <MonthlyTotalBreakdown>
                  {[
                    `${planType} ${currency} ${formatNumber(totalAmount, 2)}`,
                    telematicsStatus === "active" && isNotOldAdvancedUser
                      ? `${t("telematics")} ${currency} ${formatNumber(telematicsAddonTotal, 2)}`
                      : null,
                    wexStatus === "active"
                      ? `${t("wex_fuel_card")} ${currency} ${formatNumber(wexAddonTotal, 2)}`
                      : null,
                    equipmentStatus === "active"
                      ? `${t("small_equipment")} ${currency} ${formatNumber(equipmentAddonTotal, 2)}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" + ")}
                </MonthlyTotalBreakdown>
              </div>
              <div style={{ textAlign: "right" }}>
                <MonthlyTotalAmount>
                  {`${currency} ${formatNumber(
                    totalAmount +
                      (telematicsStatus === "active" && isNotOldAdvancedUser
                        ? telematicsAddonTotal
                        : 0) +
                      (wexStatus === "active" ? wexAddonTotal : 0) +
                      (equipmentStatus === "active" ? equipmentAddonTotal : 0),
                    2,
                  )}`}
                </MonthlyTotalAmount>
                <MonthlyTotalPer>
                  {inputDuration === 2 ? t("per_year") : t("per_month")}
                </MonthlyTotalPer>
              </div>
            </MonthlyTotalCard>
          </Col>
        </Row>
      )}

      {/* Blue Card Text */}
      {/* {blueCardText && (
        <Row className="d-flex justify-content-center">
          <Col>
            <Card shadow="true">
              <CardBody style={{ background: "#9fc5f8" }}>
                <NormalSpan>
                  {blueCardText}{" "}
                  {!isAndroid && !isIos && subscriptionData.product === "5" && (
                    <LinkSpan
                      style={{ fontSize: 14 }}
                      // onClick={() =>
                      //   handleChangePlan("create_customer_session", 0)
                      // }
                      //onClick={() => setShowChangePlanModal(true)}
                      onClick={handleChangeCancel}
                    >
                      {t("click_here_to_upgrade")}
                    </LinkSpan>
                  )}
                  {!isAndroid &&
                    !isIos &&
                    ["1", "2", "3"].includes(subscriptionData.product) && (
                      <LinkSpan
                        style={{ fontSize: 14 }}
                        onClick={() => setShowComparePlans(true)}
                      >
                        {t("click_here_to_learn_more")}
                      </LinkSpan>
                    )}
                </NormalSpan>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )} */}

      <Row>
        <Col>
          <h5 style={{ fontSize: "16px" }}>{t("add_ons")}</h5>
        </Col>
      </Row>

      {(isNotOldAdvancedUser || tag === "essential-user") && (
        <Row>
          <Col>
            <TelematicsAddonRow
              status={telematicsStatus}
              isSubscriptionActive={
                subscriptionData?.stripe_status === "active"
              }
              isMobileSubscription={isAndroid || isIos}
              vehicleCount={vehicleCount}
              unitPrice={telematicsUnitPrice}
              currency={priceDetailsData?.currency_code || ""}
              inputDuration={inputDuration}
              planName={planType}
              totalVehicleSubAmount={totalAmount}
              periodEndDate={telematicsEndDate}
              totalAfterCancellation={telematicsTotalAfterCancellation}
              wexStatus={wexStatus}
              equipmentStatus={equipmentStatus}
              wexUnitPrice={wexUnitPrice}
              equipmentUnitPrice={equipmentUnitPrice}
              equipmentQuantity={subscriptionData?.no_of_equipments || 0}
              onAddSuccess={() =>
                pollSubscriptionStatus(
                  (data) => data?.telematics_stripe_status === "active",
                  5,
                  3000,
                  () => showToast("success", t("plan_updated_successfully")),
                )
              }
              onCancelSuccess={() =>
                pollSubscriptionStatus(
                  (data) => data?.telematics_stripe_status === "canceled",
                  5,
                  3000,
                  () => showToast("success", t("plan_updated_successfully")),
                )
              }
              setShowMobileAddOnErrPopup={setShowMobileAddOnErrPopup}
              paymentData={paymentData}
              currentProductId={subscriptionData?.product}
            />
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <WexAddonRow
            status={wexStatus}
            isSubscriptionActive={subscriptionData?.stripe_status === "active"}
            isMobileSubscription={isAndroid || isIos}
            vehicleCount={vehicleCount}
            unitPrice={wexUnitPrice}
            currency={priceDetailsData?.currency_code || ""}
            inputDuration={inputDuration}
            planName={planType}
            totalVehicleSubAmount={totalAmount}
            periodEndDate={wexEndDate}
            totalAfterCancellation={wexTotalAfterCancellation}
            equipmentStatus={equipmentStatus}
            equipmentUnitPrice={equipmentUnitPrice}
            telematicsUnitPrice={telematicsUnitPrice}
            telematicsStatus={telematicsStatus}
            isNotOldAdvancedUser={isNotOldAdvancedUser}
            equipmentQuantity={subscriptionData?.no_of_equipments || 0}
            onAddSuccess={() =>
              pollSubscriptionStatus(
                (data) => data?.fuel_card_stripe_status === "active",
                5,
                3000,
                () => showToast("success", t("plan_updated_successfully")),
              )
            }
            onCancelSuccess={() =>
              pollSubscriptionStatus(
                (data) => data?.fuel_card_stripe_status === "canceled",
                5,
                3000,
                () => showToast("success", t("plan_updated_successfully")),
              )
            }
            setShowMobileAddOnErrPopup={setShowMobileAddOnErrPopup}
            isWexEligiblePlan={isWexEligiblePlan}
            setShowWexPlanErrPopup={setShowWexPlanErrPopup}
            paymentData={paymentData}
            currentProductId={subscriptionData?.product}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <EquipmentPurchase
            status={equipmentStatus}
            isSubscriptionActive={subscriptionData?.stripe_status === "active"}
            isMobileSubscription={isAndroid || isIos}
            currency={priceDetailsData?.currency_code}
            unitPrice={equipmentUnitPrice}
            inputDuration={inputDuration}
            planName={planType}
            isNewPlan={isNewPlan}
            totalVehicleSubAmount={totalAmount}
            equipmentQuantity={subscriptionData?.no_of_equipments || 0}
            periodEndDate={equipmentEndDate}
            totalAfterCancellation={equipmentTotalAfterCancellation}
            wexStatus={wexStatus}
            wexUnitPrice={wexUnitPrice}
            vehicleCount={vehicleCount}
            telematicsStatus={telematicsStatus}
            telematicsUnitPrice={telematicsUnitPrice}
            isNotOldAdvancedUser={isNotOldAdvancedUser}
            onAddSuccess={() =>
              pollSubscriptionStatus(
                (data) => data?.equipment_stripe_status === "active",
                5,
                3000,
                async () => {
                  showToast("success", t("plan_updated_successfully"));
                  const updatedVehiclesList = await getVehicles();
                  dispatch(updateVehiclesList(updatedVehiclesList));
                },
              )
            }
            onUpdateSuccess={(newCount) =>
              pollSubscriptionStatus(
                (data) => Number(data?.no_of_equipments) === Number(newCount),
                5,
                3000,
                async () => {
                  showToast("success", t("plan_updated_successfully"));
                  const updatedVehiclesList = await getVehicles();
                  dispatch(updateVehiclesList(updatedVehiclesList));
                },
              )
            }
            onCancelSuccess={() =>
              pollSubscriptionStatus(
                (data) => data?.equipment_stripe_status === "canceled",
                5,
                3000,
                () => showToast("success", t("plan_updated_successfully")),
              )
            }
            setShowMobileAddOnErrPopup={setShowMobileAddOnErrPopup}
            paymentData={paymentData}
            currentProductId={subscriptionData?.product}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <AICreditsCard
            paymentData={paymentData}
            onAddPaymentMethod={handleChangeCancel}
            creditsIncluded={creditsIncluded}
            creditsNextGrant={creditsNextGrant}
            topUpPriceDetails={topUpPriceDetails}
          />
        </Col>
      </Row>

      <Row className="d-flex justify-content-center mb-4">
        <Col>
          <TableTabs
            invoices={invoices}
            receipts={receipts}
            isAndroid={isAndroid}
            isIos={isIos}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ExistingPlan;

// ── Styled components ─────────────────────────────────────────────────────────

const MonthlyTotalCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px 24px;
  box-shadow: 2px 2px 2px lightgray;
`;

const MonthlyTotalLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9ca3af;
  margin-bottom: 4px;
`;

const MonthlyTotalBreakdown = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const MonthlyTotalAmount = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
`;

const MonthlyTotalPer = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
`;
